import * as dom from '../../util/easy-dom';
import {cmptMenu} from '../../util/cmpt-common-features';
import * as store from '../../util/store';
import * as events from '../../util/event-bus';
import * as env from '../../util/env';
import { TASK_SERVICE_ERRORS } from '../../util/constants';

/**
 * Displays tasks
 * @class Sidekick.Tasks
 * @memberOf Sidekick
 * @param {EasyDom} elem - Container {@link EasyDom} element
 * @param {Object} attrs
 * @param {Object} ctx
 * @param {Function} i18n - translation function
 * @emits task.delete
 * @emits task.mark-as-done
 * @listens store tasks
 * @example Array of tasks
 * title: Task Title
 * comment: Optional. Task detail text
 * priority: Task priority
 * assignee: Task assignee
 * assignTo: Task target user
 * dueDate: Task due date
 * redirectUrl: Task redirect URL
 * completed: Shows the task as completed
 * {
	title: 'this is task 1',
  comment: 'You have a new task...',
  priority: 'High',
  dueDate: '2026-07-20',
  redirectUrl: '/tasks/1',
  completed: false
}, {
	title: 'this is task 2',
  comment: 'You have a new task... again!',
  priority: 'Medium',
  dueDate: '2026-07-22',
  redirectUrl: '/tasks/2',
  completed: true
}
 */
export default function (elem, attrs, ctx, i18n) {
  const { responsiveTopNav } = store.get('sk-config');
  setViewMoreTasksLink();

  // Find DOM elements
  const domElements = {
    desktop: {
      cont: elem.find('.menulist')[0],
      menu: elem.find('.menu')[0],
      task: elem.find('.control')[0],
      unreadBadge: elem.find('.control .sk--unread')[0],
      menuCloseIcon: elem.find('.ss-ctrl-clear')[0],
      viewMoreLink: elem.find('.menu .view-more-tasks-btn')[0]
    },
    mobile: {
      cont: elem.find('.menulist-mobile')[0],
      menu: elem.find('.menu-tasks-mobile')[0],
      task: elem.find('.control-responsive-tasks')[0],
      unreadBadge: elem.find('.control-responsive-tasks .sk--unread')[0],
      taskBtn: elem.find('.control-responsive-tasks')[0],
      viewMoreLink: elem.find('.menu-tasks-mobile .view-more-tasks-btn')[0]
    },
  };

  const domButtonElements = document.querySelectorAll('.tasks-btn');
  const domMenuElements = document.querySelectorAll('.menu-tasks');

  if (responsiveTopNav) {
    domButtonElements.forEach((domButtonElement) => {
      domButtonElement.classList.add('sk-task-responsive');
    });
    domMenuElements.forEach((domMenuElement) => {
      domMenuElement.classList.add('sk-task-menu-responsive');
    });
    if (attrs.role === 'navitem') {
      domElements.mobile.taskBtn.css('display', 'block');
      if (ctx.url != null) {
        domElements.mobile.task.attr('target', ctx.target);
        events.on('tasks.unread-count', setUnreadBadge, true);
        return;
      }
      domElements.mobile.taskBtn.on('click', (evt) => {
        evt.stopPropagation();
        evt.preventDefault();
        const displayMobileMenu = domElements.mobile.menu.css('display');
        const displayAction = displayMobileMenu === 'none'? 'block' : 'none';
        domElements.mobile.menu.css('display', displayAction);
      });
    }
  }

  // Handle initial state and data changes
  if (ctx.url != null) {
    domElements.desktop.task.attr('target', ctx.target);
    events.on('tasks.unread-count', setUnreadBadge, true);
    return;
  }

  store.onValue('tasks', updateAllTasks);
  events.on('tasks.unread-count', setUnreadBadge, true);

  // Listen for events and take action
  cmptMenu(elem, domElements.desktop.task, domElements.desktop.menu);
  domElements.desktop.menuCloseIcon.on('click', () => events.emit('sk-menu-close', elem));
  handleMenuEvent(domElements.desktop);
  if(responsiveTopNav) {
    handleMenuEvent(domElements.mobile);
  }

  function escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function setViewMoreTasksLink() {
    const taskPageUrl = getTasksPageUrl();
    elem.find('.view-more-tasks-btn').forEach((linkEl) => {
      linkEl.attr('href', taskPageUrl);
    });
  }

  function getTasksPageUrl() {
    let envName = 'prod';
    const passportData = store.get('passport-data');
    const passportCfg = store.get('passport');

    if (passportCfg !== null && passportData !== null) {
      envName = env.environmentFromPassportDomain(window.location.hostname);
    }

    const envValues = env[envName] || env.prod;
    return envValues.tasksPageUrl || '/tasks';
  }

  // Update function for when task list changes
  function updateTaskList(newCtx, elements) {

    // Handle error scenarios
    if (newCtx && newCtx.error) {
      const domTasksError = dom.parse(`<li class="menulist-item" ><div style="color: red;">${i18n(TASK_SERVICE_ERRORS.DEFAULT)}</div></li>`);
      elements.cont.children(domTasksError);
      // Immediately clear the badge when error is detected
      setUnreadBadge(0);
      return;
    }

		// update our context object
    if (!ctx || ctx.items !== newCtx.items) { ctx = newCtx; }
    const domTasksEmpty = dom.parse(`<li class="menulist-item tasks-empty"><div>${i18n('You have no tasks')}</div></li>`);
    const incompleteItems = ctx.items.filter(item => !item.completed);

    // No actionable items to display
    if (incompleteItems.length === 0) { elements.cont.children(domTasksEmpty); return; }

    // Process the list of items to display (filter out completed tasks)
    elements.cont.children(incompleteItems.map(item => {
      const taskMeta = getTaskMetaMarkup(item);
      const el = dom.parse(`
        <li class="menulist-item ${item.completed ? 'task-completed' : 'task-incomplete'} ${item.redirectUrl ? 'task-has-link' : ''}">
          <h4 class="subject">
            ${escapeHtml(item.title)}
          </h4>
          ${item.comment ? `<div class="task-comment">${escapeHtml(item.comment)}</div>` : ''}
          ${taskMeta}
          <button type="button" class="remove fa-li-close" aria-label="remove task"></button>
        </li>
      `);

			el.find('.remove')[0].on('click', evt => {
				evt.preventDefault();
				evt.stopPropagation();
        events.emit('task.delete', item);
			});

			el.on('click', function (evt) {
				evt.stopPropagation();
				if (!item.completed) {
					events.emit('task.mark-as-done', item);
				}
				if (item.redirectUrl) {
					window.open(item.redirectUrl, '_blank', 'noopener,noreferrer');
				}
				events.emit('task.action', item);
			});

			return el;
		}));

    if (!elements.menu.hasClass('ss-menu-open')) {
      elements.cont.find('=a').forEach(a => a.attr('tabindex', '-1'));
      elements.cont.find('=button').forEach(b => b.attr('tabindex', '-1'));
    }
  }

  function updateAllTasks(newCtx) {
    const normalizedCtx = newCtx && newCtx.error ? newCtx : normalizeTasksContext(newCtx);
    updateIncomplete(normalizedCtx);
    updateTaskList(normalizedCtx, domElements.desktop);
    if(responsiveTopNav) {
      updateTaskList(normalizedCtx, domElements.mobile);
    }
  }

  function updateIncomplete(taskContext) {
    // Don't update count if there's an error
    if (taskContext && taskContext.error) {
      setUnreadBadge(0);
      return;
    }

    const items = taskContext && taskContext.items ? taskContext.items : [];
    let incomplete = items.filter(task => task.completed !== true).length;
    setUnreadBadge(incomplete);
  }

  function normalizeTasksContext(taskContext) {
    if (Object.prototype.toString.call(taskContext) === '[object Array]') {
      taskContext = { items: taskContext };
    }

    if (!taskContext || Object.prototype.toString.call(taskContext.items) !== '[object Array]') {
      return { items: [] };
    }

    const normalizedItems = taskContext.items.map((item, index) => normalizeTaskItem(item, index));
    const sortedItems = sortTasksByDueDate(normalizedItems);
    return Object.assign({}, taskContext, { items: sortedItems });
  }

  function normalizeTaskItem(item = {}, sourceIndex) {
    const completed = typeof item.completed === 'boolean'
      ? item.completed
      : (typeof item.done === 'boolean' ? item.done : false);

    return Object.assign({}, item, {
      _sourceIndex: sourceIndex,
      title: item.title || i18n('Untitled task'),
      comment: item.comment != null ? item.comment : (item.content || ''),
      priority: item.priority || '',
      assignee: item.assignee || '',
      assignTo: item.assignTo || '',
      dueDate: item.dueDate || item.timestamp || '',
      redirectUrl: item.redirectUrl || item.url || '',
      completed
    });
  }

  function sortTasksByDueDate(items) {
    return items.slice().sort((itemA, itemB) => {
      const dueDateA = getDueDateTimestamp(itemA.dueDate);
      const dueDateB = getDueDateTimestamp(itemB.dueDate);

      if (dueDateA !== null && dueDateB !== null && dueDateA !== dueDateB) {
        return dueDateA - dueDateB;
      }
      if (dueDateA !== null && dueDateB === null) {
        return -1;
      }
      if (dueDateA === null && dueDateB !== null) {
        return 1;
      }

      return itemA._sourceIndex - itemB._sourceIndex;
    });
  }

  function getDueDateTimestamp(dueDate) {
    if (!dueDate) {
      return null;
    }

    const timestamp = new Date(dueDate).getTime();
    return Number.isNaN(timestamp) ? null : timestamp;
  }

  function getTaskMetaMarkup(item) {
    const metaRows = [];

    if (item.priority) {
      metaRows.push(`<small class="task-meta"><span class="task-meta-label">${i18n('Priority')}:</span> ${escapeHtml(item.priority)}</small>`);
    }

    if (item.assignee) {
      metaRows.push(`<small class="task-meta"><span class="task-meta-label">${i18n('Assignee')}:</span> ${escapeHtml(item.assignee)}</small>`);
    }

    if (item.assignTo) {
      metaRows.push(`<small class="task-meta"><span class="task-meta-label">${i18n('Assign To')}:</span> ${escapeHtml(item.assignTo)}</small>`);
    }

    if (item.dueDate) {
      metaRows.push(`<small class="task-meta"><span class="task-meta-label">${i18n('Due Date')}:</span> ${escapeHtml(item.dueDate)}</small>`);
    }

    return metaRows.join('');
  }

  function setUnreadBadge(unread) {
    elem.toggleClass('has-unread', unread > 0);
    updateUnreadBadgeElement(domElements.desktop.unreadBadge, unread);

    if(responsiveTopNav) {
      updateUnreadBadgeElement(domElements.mobile.unreadBadge, unread);
    }
  }

  function updateUnreadBadgeElement(unreadBadgeElem, unreadCount) {
    unreadBadgeElem.css('display', 'none');
    unreadBadgeElem.removeClass('sk--max');

    if (unreadCount <= 0) {
      return;
    }

    const displayCount = unreadCount > 99 ? 99 : unreadCount;
    if (unreadCount > 99) {
      unreadBadgeElem.addClass('sk--max');
    }

    unreadBadgeElem._.innerHTML = displayCount;
    unreadBadgeElem.css('display', 'block');
  }

  function handleMenuEvent(elements) {
    events.on('sk-menu-close', function (e) {
      if (e !== elem) {
        return;
      }
      elements.cont.find('=a').forEach((a) => a.attr('tabindex', '-1'));
      elements.cont.find('=button').forEach((b) => b.attr('tabindex', '-1'));
      if (elements.viewMoreLink) {
        elements.viewMoreLink.attr('tabindex', '-1');
      }
    });
    events.on('sk-menu-open', function (e) {
      if (e !== elem) {
        return;
      }
      elements.cont.find('=a').forEach((a) => a.attr('tabindex', '0'));
      elements.cont.find('=button').forEach((b) => b.attr('tabindex', '0'));
      if (elements.viewMoreLink) {
        elements.viewMoreLink.attr('tabindex', '0');
      }
    });
  }
}
